import { useColorScheme } from "react-native";
import {
  FontFamilyTokens,
  SizeTokens,
  Token,
  getConfig,
  getTokenValue,
  useTheme,
} from "tamagui";

import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { ThemeSettings, useThemeStore } from "@/store/theme";

export type ResolvedTheme = ThemeSettings.Light | ThemeSettings.Dark;

let resolvedThemeSnapshot: ResolvedTheme = ThemeSettings.Light;

export const setResolvedThemeSnapshot = (theme: ResolvedTheme) => {
  resolvedThemeSnapshot = theme;
};

const resolveThemeChoice = (
  themeChoice: ThemeSettings,
  systemTheme: ReturnType<typeof useColorScheme>,
): ResolvedTheme => {
  return themeChoice === ThemeSettings.System
    ? systemTheme === "dark"
      ? ThemeSettings.Dark
      : ThemeSettings.Light
    : themeChoice;
};

/**
 * Non-reactive snapshot for imperative setup code only.
 *
 * Do not use this from React components, hooks, or render-time helpers.
 * It reads the theme applied by the provider, defaulting to light before setup.
 * Use `useResolvedTheme()` to react to flag or preference changes.
 */
export const getResolvedTheme = () => {
  return resolvedThemeSnapshot;
};

export const useResolvedTheme = () => {
  const themeChoice = useThemeStore((state) => state.themeChoice);
  const systemTheme = useColorScheme();
  const { value: defaultToLight } = useFeatureFlag(
    "default-to-light-mode",
    true,
  );

  if (themeChoice === undefined && defaultToLight) {
    return ThemeSettings.Light;
  }
  return resolveThemeChoice(themeChoice ?? ThemeSettings.System, systemTheme);
};

// Retrieves the value of a font token from the config, or the value itself if it's not a token
export const getFontLineHeight = (
  fontFamily: FontFamilyTokens,
  token: SizeTokens | string,
) => {
  const config = getConfig();
  const font = config.fontsParsed[fontFamily];
  const lineHeight = font?.lineHeight?.[token];
  if (typeof lineHeight === "number") {
    return lineHeight;
  }
  return lineHeight?.val;
};

// Retrieves the value of a color token from the theme, or the color itself if it's not a token
export const useGetColor = (color: string) => {
  const theme = useTheme();
  if (color.charAt(0) === "$") {
    const val = theme[color]?.get();
    return val;
  }
  return color;
};

export const useIsDarkTheme = () => {
  return useResolvedTheme() === ThemeSettings.Dark;
};

/** Returns shadow props for consistent drop shadows across components */
export const getShadow = ({
  x = 0,
  y = 4,
  blur = "32px",
  spread = "", // We purposely use 0px to visually emulate figma designs, even though figma uses -10px
}: {
  x?: Token | number;
  y?: Token | number;
  blur?: Token | number | string;
  spread?: Token | number | string;
} = {}) => {
  if (getResolvedTheme() === ThemeSettings.Dark) return {};

  const resolvedBlur = getTokenValueOrFallback(blur);
  const resolvedSpread = spread ? getTokenValueOrFallback(spread) : undefined;
  return {
    shadowColor: "$shadowColor",
    shadowOffset: {
      width: getTokenValueOrFallback(x) as number,
      height: getTokenValueOrFallback(y) as number,
    },
    shadowRadius: resolvedSpread
      ? `${resolvedBlur} ${resolvedSpread}`
      : resolvedBlur,
  };
};

// Returns the value a token represents, or the value itself if it's not a token
export const getTokenValueOrFallback = (
  value: Token | number | string | undefined,
) => {
  if (value?.toString().charAt(0) === "$") {
    return getTokenValue(value as Token);
  }
  return value;
};
